/**
 * Admin Overview page — system summary dashboard.
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import * as adminService from '../../services/admin.service';
import { Users, Key, Coins, Activity, ArrowRight } from 'lucide-react';

export default function AdminOverviewPage() {
  const [metrics, setMetrics] = useState<adminService.AdminMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    adminService
      .getSystemMetrics()
      .then(setMetrics)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="page-loader">Loading admin dashboard…</div>;
  if (error) return <div className="alert alert-error">{error}</div>;

  return (
    <div className="dashboard-page">
      <div className="page-header">
        <div>
          <h1>Admin Overview</h1>
          <p>System-wide metrics and management</p>
        </div>
      </div>

      <div className="metrics-grid">
        <MetricCard icon={<Users size={20} />} label="Total Developers" value={String(metrics?.totalDevelopers ?? 0)} />
        <MetricCard icon={<Activity size={20} />} label="Total Users" value={String(metrics?.totalUsers ?? 0)} />
        <MetricCard icon={<Key size={20} />} label="Total API Keys" value={String(metrics?.totalApiKeys ?? 0)} />
        <MetricCard icon={<Coins size={20} />} label="Total User Tokens" value={String(metrics?.totalUserTokens ?? 0)} />
      </div>

      <h2 className="section-title">Quick Actions</h2>
      <div className="quick-actions">
        <Link to="/admin/developers" className="quick-action-card">
          <Users size={20} />
          <span>Onboard / Manage Developers</span>
          <ArrowRight size={16} />
        </Link>
        <Link to="/admin/users" className="quick-action-card">
          <Coins size={20} />
          <span>Manage Users &amp; Tokens</span>
          <ArrowRight size={16} />
        </Link>
        <Link to="/admin/usage-logs" className="quick-action-card">
          <Activity size={20} />
          <span>Usage Logs</span>
          <ArrowRight size={16} />
        </Link>
        <Link to="/admin/metrics" className="quick-action-card">
          <Key size={20} />
          <span>System Metrics</span>
          <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  );
}

function MetricCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="metric-card">
      <div className="metric-icon">{icon}</div>
      <div className="metric-body">
        <span className="metric-value">{value}</span>
        <span className="metric-label">{label}</span>
      </div>
    </div>
  );
}
